<template>
  <v-dialog :value="value" max-width="680px" persistent scrollable @input="$emit('input', $event)">
    <v-card class="pbd">
      <div class="pbd__head">
        <div>
          <div class="pbd__title">Dados bancários</div>
          <div class="pbd__subtitle">
            Conta para receber os pagamentos de <strong class="text-capitalize">{{ creditorName || "credor" }}</strong> no caso escolhido.
          </div>
        </div>
        <v-btn icon small :disabled="saving" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <v-card-text class="pbd__body">
        <v-progress-linear v-if="loading" indeterminate class="mt-4" />
        <v-alert v-else-if="error" type="error" text class="mt-4 mb-0">{{ error }}</v-alert>

        <v-form v-else ref="form" v-model="valid">
          <section class="pbd__section">
            <div class="pbd__label">Caso</div>
            <v-select
              v-model="form.client_id"
              :items="caseItems"
              item-text="client_name"
              item-value="client_id"
              item-disabled="disabled"
              outlined
              dense
              hide-details="auto"
              :rules="[$rules.required]"
              no-data-text="Nenhum caso com associação aprovada"
              @change="fillFromCase"
            >
              <template #item="{ item }">
                <div class="pbd__case-item">
                  <span>{{ item.client_name }}</span>
                  <span v-if="item.disabled" class="pbd__case-note">envio de dados bancários fechado</span>
                  <span v-else-if="item.bank" class="pbd__case-note pbd__case-note--ok">já cadastrado</span>
                </div>
              </template>
            </v-select>
            <div v-if="current && current.bank" class="pbd__hint">
              Atualizado em {{ current.bank.updated_at | defaultDate("DD/MM/YYYY [às] HH:mm") }}. Salvar substitui a conta deste caso.
            </div>
          </section>

          <section v-if="form.client_id" class="pbd__section">
            <div class="pbd__label">De quem é a conta</div>
            <div class="pbd__choice" role="radiogroup" aria-label="De quem é a conta">
              <button type="button" role="radio" class="pbd__option" :class="{ 'pbd__option--on': form.holder === 'creditor' }"
                :aria-checked="String(form.holder === 'creditor')" @click="setHolder('creditor')">
                <v-icon size="20">mdi-account-outline</v-icon>
                <span>
                  <strong>Conta do credor</strong>
                  <small class="text-capitalize">{{ creditorName }}</small>
                </span>
              </button>
              <button type="button" role="radio" class="pbd__option" :class="{ 'pbd__option--on': form.holder === 'procurator' }"
                :aria-checked="String(form.holder === 'procurator')" @click="setHolder('procurator')">
                <v-icon size="20">mdi-briefcase-account-outline</v-icon>
                <span>
                  <strong>Minha conta (procurador)</strong>
                  <small>{{ procuratorName }}</small>
                </span>
              </button>
            </div>
            <div v-if="form.holder === 'procurator'" class="pbd__hint">
              Você fica cadastrado como procurador do credor, com a procuração aprovada neste caso.
            </div>
          </section>

          <template v-if="form.client_id && form.holder">
            <section class="pbd__section">
              <div class="pbd__label">Banco e conta</div>
              <v-row dense>
                <v-col cols="12" sm="8">
                  <v-text-field dense outlined label="Nome do banco" v-model="form.bank_name" :rules="[$rules.required]" hide-details="auto" />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field dense outlined label="Número do banco" v-model="form.bank_code" :rules="[$rules.required]" hide-details="auto" />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field dense outlined label="Agência" v-model="form.agency" :rules="[$rules.required]" hide-details="auto" />
                </v-col>
                <v-col cols="8" sm="4">
                  <v-text-field dense outlined label="Número da conta" v-model="form.account_number" :rules="[$rules.required]" hide-details="auto" />
                </v-col>
                <v-col cols="4" sm="2">
                  <v-text-field dense outlined label="Dígito" v-model="form.account_digit" hide-details="auto" />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select dense outlined label="Tipo de conta" :items="accountTypes" item-value="id" item-text="name" v-model="form.account_type"
                    :rules="[$rules.required]" hide-details="auto" />
                </v-col>
              </v-row>
            </section>

            <section class="pbd__section pbd__section--last">
              <div class="pbd__label">Titular e PIX</div>
              <v-row dense>
                <v-col cols="12" sm="5">
                  <v-text-field dense outlined label="CPF/CNPJ do titular" v-model="form.registration"
                    v-mask="['###.###.###-##', '##.###.###/####-##']" :rules="[$rules.required, validateRegistration]" hide-details="auto" />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select dense outlined label="Tipo de chave PIX" :items="pixKeyTypes" item-value="id" item-text="name" v-model="form.pix_type"
                    clearable hide-details="auto" />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field dense outlined label="Chave PIX" v-model="form.pix_key" :disabled="!form.pix_type"
                    :rules="[pixKeyRule]" hide-details="auto" />
                </v-col>
              </v-row>
            </section>
          </template>
        </v-form>
      </v-card-text>

      <div class="pbd__foot">
        <v-btn depressed class="pbd-btn pbd-btn--ghost" :disabled="saving" @click="close">Cancelar</v-btn>
        <v-btn depressed class="pbd-btn pbd-btn--primary" :loading="saving" :disabled="loading || !!error || !form.client_id || !form.holder" @click="save">
          Salvar dados bancários
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
// Dados bancarios do credor representado, por caso (so casos com associacao aprovada).
import axios from "axios";
import pixKeyTypes from "@/common/data/pix-key-types.data";

const empty = () => ({
  client_id: null,
  holder: null,
  bank_name: "",
  bank_code: "",
  agency: "",
  account_number: "",
  account_digit: "",
  account_type: null,
  registration: "",
  pix_type: null,
  pix_key: "",
});

export default {
  name: "ProcuratorBankDialog",

  props: {
    value: { type: Boolean, default: false },
    associationId: { type: [Number, String], default: null },
    clientId: { type: [Number, String], default: null },
  },

  data: () => ({
    loading: false,
    saving: false,
    error: "",
    valid: true,
    summary: null,
    form: empty(),
    pixKeyTypes,
    accountTypes: [{ id: 1, name: "Corrente" }, { id: 2, name: "Poupança" }],
  }),

  computed: {
    cases() {
      return (this.summary && this.summary.cases) || [];
    },
    caseItems() {
      return this.cases.map((item) => ({ ...item, disabled: !item.bank_enabled }));
    },
    current() {
      return this.cases.find((item) => Number(item.client_id) === Number(this.form.client_id)) || null;
    },
    creditorName() {
      return this.summary ? String(this.summary.creditor.name || "").toLowerCase() : "";
    },
    procuratorName() {
      return (this.summary && this.summary.procurator && this.summary.procurator.name) || "Você";
    },
  },

  watch: {
    value(open) {
      if (open) this.load();
    },
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      this.form = empty();
      try {
        const result = await axios.get(`/api/procurators/creditors/${this.associationId}/summary`);
        this.summary = result.data.content;
        const open = this.cases.filter((item) => item.bank_enabled);
        const wanted = this.cases.find((item) => Number(item.client_id) === Number(this.clientId) && item.bank_enabled);
        const initial = wanted || (open.length === 1 ? open[0] : null);
        if (initial) {
          this.form.client_id = initial.client_id;
          this.fillFromCase();
        }
      } catch (error) {
        this.error = this.$codes(error);
      } finally {
        this.loading = false;
      }
    },

    // Conta ja cadastrada no caso vem preenchida; sem conta, comeca vazia.
    fillFromCase() {
      const bank = this.current && this.current.bank;
      const clientId = this.form.client_id;
      if (!bank) {
        this.form = { ...empty(), client_id: clientId };
        return;
      }
      this.form = {
        client_id: clientId,
        holder: bank.holder,
        bank_name: bank.bank_name || "",
        bank_code: bank.bank_code || "",
        agency: bank.agency || "",
        account_number: bank.account_number || "",
        account_digit: bank.account_digit || "",
        account_type: bank.account_type,
        registration: this.formatRegistration(bank.registration),
        pix_type: bank.pix_type,
        pix_key: bank.pix_key || "",
      };
    },

    // Ao trocar o titular, sugere o CPF/CNPJ dele (sem apagar o que ja foi digitado para outro).
    setHolder(holder) {
      this.form.holder = holder;
      const creditorRegistration = this.summary ? this.summary.creditor.registration : "";
      const procuratorRegistration = (this.summary && this.summary.procurator && this.summary.procurator.registration) || "";
      const known = [creditorRegistration, procuratorRegistration].map((item) => String(item || "").replace(/\D/g, ""));
      const typed = String(this.form.registration || "").replace(/\D/g, "");
      if (!typed || known.includes(typed)) {
        this.form.registration = this.formatRegistration(holder === "creditor" ? creditorRegistration : procuratorRegistration);
      }
    },

    formatRegistration(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return digits;
    },

    validateRegistration(value) {
      if (!value) return true;
      return String(value).replace(/\D/g, "").length <= 11 ? this.$rules.cpf(value) : this.$rules.cnpj(value);
    },

    pixKeyRule(value) {
      const type = Number(this.form.pix_type);
      if (!type) return true;
      if (!value) return "Informe a chave PIX.";
      const digits = String(value).replace(/\D/g, "");
      if (type === 1) return digits.length === 11 || "Chave CPF deve ter 11 dígitos.";
      if (type === 2) return digits.length === 14 || "Chave CNPJ deve ter 14 dígitos.";
      if (type === 4) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || "E-mail inválido.";
      if (type === 5) return String(value).length === 32 || "Chave aleatória deve ter 32 caracteres.";
      return true;
    },

    close() {
      this.$emit("input", false);
    },

    async save() {
      if (!this.$refs.form || !this.$refs.form.validate()) return;
      this.saving = true;
      try {
        const result = await axios.put(`/api/procurators/creditors/${this.associationId}/bank-information`, {
          ...this.form,
          registration: String(this.form.registration || "").replace(/\D/g, ""),
          pix_type: this.form.pix_type || null,
          pix_key: this.form.pix_type ? this.form.pix_key : null,
        });
        this.$dialog.notify.success("Dados bancários salvos.", { position: "top-right", timeout: 3000 });
        this.$emit("saved", result.data.content);
        this.close();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pbd { border-radius: 14px !important; overflow: hidden; }
.pbd__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 20px 24px 16px; border-bottom: 1px solid #ece8f2; }
.pbd__title { font-size: 20px; font-weight: 700; color: #1d0b33; line-height: 1.3; }
.pbd__subtitle { margin-top: 2px; font-size: 13px; color: #6b6478; }
.pbd__body { padding: 0 24px 4px !important; }
.pbd__section { padding: 18px 0; border-bottom: 1px solid #f0edf4; }
.pbd__section--last { border-bottom: 0; }
.pbd__label { margin-bottom: 10px; font-size: 14px; font-weight: 700; color: #1d0b33; }
.pbd__hint { margin-top: 8px; font-size: 12.5px; color: #6b6478; }
.pbd__case-item { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; }
.pbd__case-note { font-size: 11.5px; color: #8c8597; }
.pbd__case-note--ok { color: #1e7b4f; }
.pbd__choice { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.pbd__option { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1px solid #ddd5e8; border-radius: 12px; background: #fff; text-align: left; transition: border-color 0.15s, background 0.15s; }
.pbd__option:hover { border-color: #7e55ab; }
.pbd__option .v-icon { color: #7e55ab; }
.pbd__option span { display: flex; flex-direction: column; min-width: 0; }
.pbd__option strong { font-size: 14px; color: #1d0b33; }
.pbd__option small { font-size: 12px; color: #6b6478; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pbd__option--on { border-color: #7e55ab; background: rgba(126, 85, 171, 0.07); box-shadow: inset 0 0 0 1px #7e55ab; }
.pbd__foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 24px; border-top: 1px solid #ece8f2; background: #fbfafc; }
.pbd-btn { height: 40px !important; padding: 0 18px !important; border-radius: 10px !important; text-transform: none !important; letter-spacing: 0 !important; font-size: 14px !important; font-weight: 600 !important; }
.pbd-btn--primary { background: #1d0b33 !important; color: #fff !important; }
.pbd-btn--primary.v-btn--disabled { background: #e7e3ed !important; color: #9a93a6 !important; }
.pbd-btn--ghost { background: transparent !important; color: #4b2a75 !important; }
.pbd-btn--ghost:hover { background: rgba(126, 85, 171, 0.08) !important; }
.pbd ::v-deep .v-text-field--outlined fieldset { border-radius: 10px; border-color: #d9d2e3; }
@media (max-width: 600px) {
  .pbd__head, .pbd__foot { padding-left: 16px; padding-right: 16px; }
  .pbd__body { padding-left: 16px !important; padding-right: 16px !important; }
  .pbd__choice { grid-template-columns: 1fr; }
}
</style>
